import type { AxiosRequestConfig } from "axios";
import type {
  IPaginated,
  IRepositoryOptions,
  IValidatedDocument,
} from "@/types/common";
import type {
  IInspector,
  IInspectorHud,
  IInspectorLicense,
  IInspectorW9Form,
} from "@/types/users-inspectors";
import { BaseRepository } from "../BaseRepository";
import type { InspectorsService } from "./service";
import type {
  IDeleteInspectorRequest,
  IGetInspectorProfileByUserIdRequest,
  IGetInspectorsRequest,
  IUpdateInspectorRequest,
  IUpdateInspectorServiceAreasRequest,
  IUpsertHudDetailsRequest,
  IUpsertHudDetailsResponse,
  IUpsertLicenseDetailsRequest,
  IUpsertW9DetailsRequest,
  IUpsertW9DetailsResponse,
  IValidateHudIdInput,
  IValidateHudIdRequest,
  IValidateLicenseInput,
  IValidateLicenseRequest,
  IValidateW9FormInput,
  IValidateW9FormRequest,
} from "./types";

export class InspectorsRepository extends BaseRepository {
  constructor(
    private readonly service: InspectorsService,
    options?: IRepositoryOptions,
  ) {
    super(options);
  }

  async getAll(
    p: IGetInspectorsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IPaginated<IInspector>> {
    const response = await this.handleRequest(this.service.getAll, p, c);
    return response.data;
  }

  async deleteInspector(
    p: IDeleteInspectorRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    await this.handleRequest(this.service.deleteInspector, p, c);
  }

  async updateInspector(
    p: IUpdateInspectorRequest,
    c?: AxiosRequestConfig,
  ): Promise<IInspector> {
    const response = await this.handleRequest(
      this.service.updateInspector,
      p,
      c,
    );
    return response.data;
  }

  async upsertLicenseDetails(
    p: IUpsertLicenseDetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IInspectorLicense> {
    const p2 = new FormData();

    p2.append(
      "license",
      new Blob([JSON.stringify(p.license)], { type: "application/json" }),
    );

    p2.append("frontImage", p.frontImage);
    p2.append("backImage", p.backImage);

    const response = await this.handleRequest(
      this.service.upsertLicenseDetails,
      p2,
      c,
    );

    return response.data;
  }

  async validateLicenseDetails(
    p: IValidateLicenseInput,
    c?: AxiosRequestConfig,
  ): Promise<IValidatedDocument<IInspectorLicense>> {
    const p2: IValidateLicenseRequest = {
      side: p.side,
      formData: new FormData(),
    };

    p2.formData.append("licenseImage", p.file);

    const response = await this.handleRequest(
      this.service.validateLicenseDetails,
      p2,
      c,
    );

    return response.data;
  }

  async upsertHudDetails(
    p: IUpsertHudDetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUpsertHudDetailsResponse> {
    const p2 = new FormData();

    p2.append(
      "hudDetail",
      new Blob([JSON.stringify(p.hudDetail)], { type: "application/json" }),
    );

    p2.append("frontImage", p.frontImage);
    p2.append("backImage", p.backImage);

    const response = await this.handleRequest(
      this.service.upsertHudDetails,
      p2,
      c,
    );

    return response.data;
  }

  async validateHudDetails(
    p: IValidateHudIdInput,
    c?: AxiosRequestConfig,
  ): Promise<IValidatedDocument<IInspectorHud>> {
    const p2: IValidateHudIdRequest = {
      side: p.side,
      formData: new FormData(),
    };

    p2.formData.append("hudImage", p.file);

    const response = await this.handleRequest(
      this.service.validateHudDetails,
      p2,
      c,
    );

    return response.data;
  }

  async upsertW9Details(
    p: IUpsertW9DetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUpsertW9DetailsResponse> {
    const p2 = new FormData();

    p2.append(
      "w9Form",
      new Blob([JSON.stringify(p.w9Form)], { type: "application/json" }),
    );

    if (p.frontImage) {
      p2.append("frontImage", p.frontImage);
    }

    const response = await this.handleRequest(
      this.service.upsertW9Details,
      p2,
      c,
    );

    return response.data;
  }

  async validateW9Details(
    p: IValidateW9FormInput,
    c?: AxiosRequestConfig,
  ): Promise<IValidatedDocument<IInspectorW9Form>> {
    const p2: IValidateW9FormRequest = {
      side: p.side,
      formData: new FormData(),
    };

    p2.formData.append("w9FormImage", p.file);

    const response = await this.handleRequest(
      this.service.validateW9Details,
      p2,
      c,
    );

    return response.data;
  }

  async getProfileByUserId(
    p: IGetInspectorProfileByUserIdRequest,
    c?: AxiosRequestConfig,
  ): Promise<IInspector> {
    const response = await this.handleRequest(
      this.service.getProfileByUserId,
      p,
      c,
    );
    return response.data;
  }

  async updateInspectorServiceArea(
    p: IUpdateInspectorServiceAreasRequest,
    c?: AxiosRequestConfig,
  ): Promise<IInspector> {
    const response = await this.handleRequest(
      this.service.updateInspectorServiceArea,
      p,
      c,
    );

    return response.data;
  }
}
